// Closest PMS (Pantone solid coated) for a logo color read off the uploaded
// image (Stephanie 2026-10-15: "can you also notate the PMS numbers?").
// The colors come from a screen image, so this is the NEAREST match by eye
// (CIE Lab distance), not a confirmed PMS — art still confirms for print.
// Color list: the MIT "pantone-colors" package (907 PMS numbers → hex).
import pantone from "pantone-colors";

type Lab = [number, number, number];

function toLab(hex: string): Lab | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(m[1]!.slice(i, i + 2), 16) / 255;
    return c > 0.04045 ? ((c + 0.055) / 1.055) ** 2.4 : c / 12.92;
  }) as [number, number, number];
  const x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
  const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
  const z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
}

const TABLE: Array<[string, Lab]> = Object.entries(pantone as Record<string, string>).flatMap(([pms, hex]) => {
  const lab = toLab(hex);
  return lab ? [[pms, lab] as [string, Lab]] : [];
});

/** "PMS 360 C" for "#7cc24e"; null for anything that isn't a hex color. */
export function nearestPms(hex: string): string | null {
  const a = toLab(hex);
  if (!a) return null;
  let best: string | null = null;
  let bestD = Infinity;
  for (const [pms, b] of TABLE) {
    const d = (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;
    if (d < bestD) {
      bestD = d;
      best = pms;
    }
  }
  return best ? `PMS ${best} C` : null;
}
